import ConfirmDialog from "@/components/common/ConfirmDialog";
import InstructionForm from "@/components/instruction/InstructionForm";
import PageLayout from "@/components/layout/PageLayout";
import { queryCodeValues, queryUser } from "@/services/commonService";
import {
  queryInstructionDetail,
  useInstructionComplete,
  useInstructionCompleteRequest,
  useInstructionCreateOrUpdate,
  useInstructionDelete,
  useInstructionExecuteRequest,
  useInstructionReject,
} from "@/services/instructionService";
import { SUBMIT_TYPE } from "@/types/common";
import {
  INSTRUCTION_STATUS,
  INSTRUCTION_TYPE,
  InstructionFormData,
  InstructionRejectRequest,
} from "@/types/instruction";
import { getTypeLabel } from "@/utils/directiveUtils";
import { getStatusEnum, getStatusId, getTypeEnum, getTypeId } from "@/utils/typeStatusUtils";
import { useQueries } from "@tanstack/react-query";
import { useNavigate, useParams, useRouter } from "@tanstack/react-router";
import React, { useEffect, useState } from "react";

interface DirectiveFormPageProps {
  routeId: string;
}

const DirectiveFormPage: React.FC<DirectiveFormPageProps> = ({ routeId }) => {
  const navigate = useNavigate();
  const router = useRouter();
  const instructionMutationCreateOrUpdate = useInstructionCreateOrUpdate();
  const instructionMutationDelete = useInstructionDelete();
  const instructionMutationReject = useInstructionReject();
  const instructionMutationExecuteRequest = useInstructionExecuteRequest();
  const instructionMutationCompleteRequest = useInstructionCompleteRequest();
  const instructionMutationComplete = useInstructionComplete();

  const { id } = useParams({ from: routeId });
  const type = router.state.location.search.type;
  const mode = !id ? "create" : window.location.pathname.endsWith("/edit") ? "edit" : "view";

  useEffect(() => {
    if (id && isNaN(Number(id))) {
      navigate({ to: "/error/404" });
    }
  }, [id, navigate]);

  const results = useQueries({
    queries: [queryCodeValues(), queryUser(), ...(id ? [queryInstructionDetail(Number(id))] : [])],
  });

  const [codeValuesResult, userResult, instructionResult] = results;
  const codeValues = codeValuesResult.data;
  const user = userResult.data;
  const detailData = instructionResult?.data;

  // 모든 쿼리가 성공했는지 확인
  const isAllQueriesSuccess = results.every((result) => result.isSuccess);
  const typeId = type ? getTypeId(type, codeValues) : detailData?.type;
  const statusId = detailData?.status ?? getStatusId(INSTRUCTION_STATUS.WRITING, codeValues);

  // id → enum 변환 (실제 initialData는 서버에서 받아온 id 기반)
  const uiType = getTypeEnum(typeId, codeValues) || INSTRUCTION_TYPE.INSTRUCTION;
  const uiStatus = getStatusEnum(statusId, codeValues) || INSTRUCTION_STATUS.WRITING;

  // initialData는 enum(string)으로 넘김
  const initialData = id
    ? {
        ...detailData,
        type: typeId,
        status: statusId,
        uiType,
        uiStatus,
      }
    : { type: typeId, status: statusId, uiType, uiStatus, attachments: [] };

  const handleSubmit = (data: InstructionFormData, attachments: File[], submitType: string | undefined) => {
    const payload = {
      ...data,
      ownerDepartment: data.ownerDepartment?.id,
      ccDepartments: data.ccDepartments?.map((d) => d.id),
    };

    if (SUBMIT_TYPE.REGISTRATION_REQUESTED === submitType) {
      return setDialog({
        open: true,
        title: "등록요청 확인",
        content: "등록요청을 하시겠습니까?",
        onConfirm: () => {
          payload.status = getStatusId(INSTRUCTION_STATUS.PENDING_REVIEW, codeValues);
          instructionMutationCreateOrUpdate.mutate({ formData: payload, files: attachments });
        },
      });
    }

    if (SUBMIT_TYPE.SAVE === submitType) {
      return setDialog({
        open: true,
        title: "저장 확인",
        content: "저장 하시겠습니까?",
        onConfirm: () => {
          instructionMutationCreateOrUpdate.mutate({ formData: payload, files: attachments });
        },
      });
    }
  };

  const handleCancel = () => {
    const search = router.state.location.search;
    navigate({
      to: "/instructions",
      search: {
        ...search,
        type: undefined, // type은 제거하고 나머지 검색 조건은 유지
      },
    });
  };

  const [dialog, setDialog] = useState({
    open: false,
    title: "",
    content: "",
    onConfirm: () => {},
  });

  const handleDelete = (formData: InstructionFormData) => {
    setDialog({
      open: true,
      title: "삭제 확인",
      content: "정말로 삭제 하시겠습니까?",
      onConfirm: () => {
        if (!formData.id) {
          return;
        }

        instructionMutationDelete.mutate(formData.id);
      },
    });
  };

  // 이행확정
  const handleExecuteRequest = () => {
    setDialog({
      open: true,
      title: "이행확정",
      content: "이행확정 처리 하시겠습니까?",
      onConfirm: () => {
        if (!detailData.id) {
          return;
        }

        instructionMutationExecuteRequest.mutate(detailData.id);
      },
    });
  };

  // 반려
  const handleReject = (reason: string) => {
    console.log("reason", reason);

    setDialog({
      open: true,
      title: "반려",
      content: "반려 처리 하시겠습니까?",
      onConfirm: () => {
        if (!detailData.id) {
          return;
        }

        const request: InstructionRejectRequest = {
          id: detailData.id,
          reason,
        };

        instructionMutationReject.mutate(request);
      },
    });
  };

  // 완료요청
  const handleCompleteRequest = (formData: InstructionFormData, attachments: File[]) => {
    setDialog({
      open: true,
      title: "완료요청",
      content: "완료요청 하시겠습니까?",
      onConfirm: () => {
        if (!formData.id) {
          return;
        }

        instructionMutationCompleteRequest.mutate({
          request: {
            id: formData.id,
            attachments: formData.attachments ?? [],
            report: formData.report ?? "",
            reportDate: formData.reportDate ?? "",
          },
          files: attachments,
        });
      },
    });
  };

  // 완료확정
  const handleComplete = () => {
    setDialog({
      open: true,
      title: "완료확정",
      content: "완료확정 처리 하시겠습니까?",
      onConfirm: () => {
        if (!detailData.id) {
          return;
        }

        instructionMutationComplete.mutate(detailData.id);
      },
    });
  };

  return (
    <PageLayout title={getTypeLabel(uiType, mode)}>
      {isAllQueriesSuccess && (
        <>
          <InstructionForm
            id={id}
            user={user}
            detailData={detailData}
            codeValues={codeValues}
            initialData={initialData}
            onSubmit={handleSubmit}
            onDelete={() => handleDelete(detailData)}
            onCancel={handleCancel}
            onReject={handleReject}
            onExecuteRequest={handleExecuteRequest}
            onCompleteRequest={handleCompleteRequest}
            onComplete={handleComplete}
          />
          <ConfirmDialog
            open={dialog.open}
            title={dialog.title}
            content={dialog.content}
            onCancel={() => setDialog({ ...dialog, open: false })}
            onConfirm={() => {
              dialog.onConfirm();
              setDialog({ ...dialog, open: false });
            }}
          />
        </>
      )}
    </PageLayout>
  );
};

export default DirectiveFormPage;
